import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../../ampli';
import { Icon } from '../../../../components/common/icon/Icon';
import { Tooltip } from '../../../../components/common/tooltip/Tooltip';
import { postMessageToRN } from '../../../../utils/RNCommunicator/postMessageToRN.util';
import { useCheckUserLoggedIn } from '../../../../hooks/user/useCheckUserLoggedIn';
import { usePostCustomerInquiriesMutation } from '../../../../hooks/api/inquiry/mutations/usePostCustomerInquiries.mutation';
import { useGetUserProfile } from '../../../../hooks/user/useGetUserProfile';
import { PopupConfirmModal } from '../../../../components/common/modal/popup/PopupConfirmModal';
import { TelInput } from '../../../../components/common/inputs/TelInput';
import { useNotification } from '../../../../provider/ToastNotificationProvider';
import { useGetHomeIconsQuery } from '../../../../hooks/api/home/queries/useGetHomeIcons';

interface MenuCardProps {
  icon: string;
  title: string;
  tooltipEnabled: boolean;
  tooltipContent: string;
  tooltipBackgroundColor: string;
  tooltipTextColor: string;
  onClick: () => void;
}

const IconMenuCard = ({
  icon,
  title,
  tooltipEnabled,
  tooltipContent,
  tooltipBackgroundColor,
  tooltipTextColor,
  onClick,
}: MenuCardProps) => {
  return (
    <Card onClick={onClick}>
      <CardIconBox>
        {tooltipEnabled ? (
          <Tooltip
            type="narrow"
            size="small"
            customColor={{ background: tooltipBackgroundColor, text: tooltipTextColor }}
            text={tooltipContent}
            style={{ left: 'initial' }}
          >
            <IconImage src={icon} />
          </Tooltip>
        ) : (
          <IconImage src={icon} />
        )}
      </CardIconBox>
      <CardTitle>{title}</CardTitle>
    </Card>
  );
};

export const DirectMenuSection = () => {
  const navigate = useNavigate();
  const { isUserLoggedIn } = useCheckUserLoggedIn();
  const { userProfile } = useGetUserProfile();
  const { openNotification } = useNotification();

  const [userPhoneNumber, setUserPhoneNumber] = useState<string>();
  const telInputRef = useRef<HTMLInputElement | null>(null);

  const [isProductInquiryModalOpen, setIsProductInquiryModalOpen] = useState<boolean>(false);
  const [isLoginRequestModalOpen, setIsLoginRequestModalOpen] = useState<boolean>(false);

  const { data: homeIcons } = useGetHomeIconsQuery();

  const { mutate: postCustomerInquires } = usePostCustomerInquiriesMutation();

  const DISPLAYED_HOME_ICONS_PER_LINE = 4;

  const firstLineMenus = homeIcons?.slice(0, DISPLAYED_HOME_ICONS_PER_LINE);
  const secondLineMenus = homeIcons?.slice(DISPLAYED_HOME_ICONS_PER_LINE);

  const hasTooltipInSecondLine = secondLineMenus?.some((menu: any) => menu.tooltipEnabled) || false;

  const isExternalUrl = (url: string): boolean => {
    try {
      const parsedUrl = new URL(url);
      return parsedUrl.origin !== window.location.origin;
    } catch (error) {
      return false;
    }
  };

  const handleCardNavigation = (iconTitle: string, path: string) => {
    ampli.pvmClickHomeLandingIcon({
      title: iconTitle,
    });

    // TODO: 운영에서 할부문의 아이콘 삭제시 해당 로직 삭제 처리
    if (iconTitle === '할부문의') {
      handleProductInquiryButtonClick();
      return;
    }

    if (isExternalUrl(path)) {
      if (window.ReactNativeWebView) {
        postMessageToRN({ messageKey: 'link', messageParam: path });
      } else {
        window.location.href = path;
      }
      return;
    }

    const pathWithoutOrigin = path.replace(window.location.origin, '');
    navigate(pathWithoutOrigin);
  };

  const handleProductInquiryButtonClick = () => {
    ampli.pvmClickHomeLandingIcon({
      title: '할부문의',
    });
    if (!isUserLoggedIn) {
      setIsProductInquiryModalOpen(true);
      return;
    }

    postCustomerInquires(
      { reqBody: { category: 'PLUVA_HOME', phoneNumber: userProfile?.phoneNumber ?? '' } },
      {
        onSuccess: () => {
          ampli.pvmProductInquiry({ title: '할부문의', type: 'HOME', page_type: 'home' });
        },
      },
    );

    if (window.ReactNativeWebView) {
      postMessageToRN({ messageKey: 'link', messageParam: 'tel:0316074997' });
    } else {
      window.location.href = 'tel:0316074997';
    }
  };

  return (
    <>
      <DirectMenuContainer hasTooltipInSecondLine={hasTooltipInSecondLine}>
        <CardSection>
          {firstLineMenus?.map((menu) => (
            <IconMenuCard
              key={menu.id}
              icon={menu.imageUrl}
              title={menu.title}
              tooltipEnabled={menu.tooltipEnabled}
              tooltipContent={menu.tooltipContent}
              tooltipBackgroundColor={menu.tooltipBackgroundColor}
              tooltipTextColor={menu.tooltipTextColor}
              onClick={() => handleCardNavigation(menu.title, menu.landingUrl)}
            />
          ))}
        </CardSection>

        <CardSection>
          {secondLineMenus?.map((menu) => (
            <IconMenuCard
              key={menu.id}
              icon={menu.imageUrl}
              title={menu.title}
              tooltipEnabled={menu.tooltipEnabled}
              tooltipContent={menu.tooltipContent}
              tooltipBackgroundColor={menu.tooltipBackgroundColor}
              tooltipTextColor={menu.tooltipTextColor}
              onClick={() => handleCardNavigation(menu.title, menu.landingUrl)}
            />
          ))}
        </CardSection>
      </DirectMenuContainer>

      <PopupConfirmModal
        isShow={isProductInquiryModalOpen}
        hasCloseButton
        closeModal={() => {
          setIsProductInquiryModalOpen(false);
          setIsLoginRequestModalOpen(true);
          setUserPhoneNumber('');
        }}
        title={'전화번호를 남겨주세요'}
        element={
          <RequestPhoneNumberContainer>
            <RequestPhoneNumberDescription>
              {'편하실 때 연락드릴게요.\n할인 상담받아보세요.'}
            </RequestPhoneNumberDescription>

            <TelInput
              inputValue={userPhoneNumber}
              placeholder={'예) 01012341234'}
              onChange={(value: string | undefined) => setUserPhoneNumber(value)}
              inputRef={telInputRef}
            />
          </RequestPhoneNumberContainer>
        }
        buttonArray={[
          {
            text: '남기기',
            onClick: () =>
              postCustomerInquires(
                {
                  reqBody: {
                    category: 'PLUVA_HONE',
                    phoneNumber: `${userPhoneNumber?.trim()}`,
                  },
                },
                {
                  onSuccess: () => {
                    ampli.pvmProductInquiry({ title: '할부문의', type: 'HOME', page_type: 'home' });
                    setUserPhoneNumber('');
                    // 전화번호 유효성 검사는 의도적으로 생략 https://gint-team.atlassian.net/browse/PVM-1736?focusedCommentId=12928
                    if (userPhoneNumber?.length === 11) {
                      setIsProductInquiryModalOpen(false);
                      openNotification({
                        message: '상담 요청이 완료되었습니다.',
                      });
                    } else {
                      openNotification({
                        message: '전화번호를 제대로 입력하였는지 확인해 주세요.',
                      });
                    }
                  },
                },
              ),
            color: 'blue',
          },
        ]}
      />

      <PopupConfirmModal
        isShow={isLoginRequestModalOpen}
        hasCloseButton={false}
        closeModal={() => {
          setIsLoginRequestModalOpen(false);
        }}
        title={'로그인하면 회원할인이 적용되요'}
        buttonArray={[
          {
            text: '카카오톡으로 간편 로그인',
            onClick: () => {
              navigate('/oauth/login');
            },
            color: 'yellow',
            icon: <Icon name={'KakaoTalk'} width={24} height={24} />,
          },
          {
            text: '닫기',
            onClick: () => {
              setIsLoginRequestModalOpen(false);
            },
            color: 'gray',
          },
        ]}
      />
    </>
  );
};

const DirectMenuContainer = styled.div<{ hasTooltipInSecondLine: boolean }>`
  display: flex;
  max-width: 390px;
  margin: auto;
  width: 100%;

  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${(props) => (props.hasTooltipInSecondLine ? '24px' : 'var(--radius-large, 16px)')};
  align-self: stretch;

  padding: 40px 16px 0px 16px;
`;

const CardSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  align-self: stretch;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  flex: 1 0 0;
`;

const CardIconBox = styled.div`
  display: flex;
  padding: 8px;
  align-items: center;
  gap: 10px;

  border-radius: var(--radius-large, 16px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);

  cursor: pointer;
`;

const CardTitle = styled.h3`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* Display/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 18.2px */
`;

const RequestPhoneNumberContainer = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;

  gap: 24px;
`;

const RequestPhoneNumberDescription = styled.pre`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */
`;

const IconImage = styled.img`
  width: 48px;
  height: 48px;
`;
